import React, { memo, useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Monitor, X } from 'lucide-react';
import styled from 'styled-components';

import { useIsMobile } from 'core/hooks/Layout';
import { T } from 'services/ThemeTokens';

import { MobileIconButton } from '../MobileIconButton';

/**
 * ТЗ 0121, этап E — «Удобнее на компьютере».
 *
 * Плашка для экранов, которые на телефоне не переделываются (редактор
 * шаблонов договоров, матрица журнала, планирование календаря платежей):
 * экран остаётся рабочим, подсказка лишь честно говорит, что на компьютере
 * с ним удобнее. Видна только на телефоне — на планшете и компьютере
 * молекула ничего не рисует.
 *
 * Закрытие запоминается в браузере по `id` экрана: закрыл на редакторе —
 * больше там не видишь, но на журнале подсказка своя. Хранилище может быть
 * недоступно (приватный режим) — тогда плашка просто закрывается до
 * перезагрузки.
 */
export interface IDesktopHintProps {
  /** Ключ экрана — по нему помнится закрытие. */
  id: string;
  /** Своя фраза вместо общей «Этот экран удобнее открывать на компьютере». */
  text?: string;
}

const storageKey = (id: string) => `erp.desktopHint.${id}`;

const readDismissed = (id: string): boolean => {
  try {
    return window.localStorage.getItem(storageKey(id)) === '1';
  } catch {
    return false;
  }
};

export const DesktopHint = memo(({ id, text }: IDesktopHintProps) => {
  const { t } = useTranslation();
  const isMobile = useIsMobile();
  const [dismissed, setDismissed] = useState(() => readDismissed(id));

  const dismiss = useCallback(() => {
    setDismissed(true);
    try {
      window.localStorage.setItem(storageKey(id), '1');
    } catch {
      /* хранилище недоступно — закрыто до перезагрузки */
    }
  }, [id]);

  if (!isMobile || dismissed) return null;

  return (
    <Box role="note">
      <Monitor size={18} strokeWidth={2} aria-hidden="true" />
      <Body>
        <Title>{t('desktopHint.title')}</Title>
        <span>{text || t('desktopHint.text')}</span>
      </Body>
      <Close
        icon={X}
        onClick={dismiss}
        aria-label={t('desktopHint.dismiss')}
        title={t('desktopHint.dismiss')}
      />
    </Box>
  );
});

DesktopHint.displayName = 'DesktopHint';

const Box = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 10px;
  margin: 0 0 12px;
  padding: 10px 6px 10px 12px;
  border: 1px solid ${T.accentLine};
  border-radius: 10px;
  background: ${T.accentSoft};
  color: ${T.text2};
  font-size: 13px;
  line-height: 1.45;

  & > svg {
    flex: none;
    margin-top: 2px;
    color: ${T.accentText};
  }
`;

const Body = styled.div`
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

const Title = styled.span`
  font-weight: 600;
  color: ${T.text};
`;

/** Крестик — общая кнопка-значок, чуть поджата к краю плашки. */
const Close = styled(MobileIconButton)`
  margin: -8px 0;
`;
